import type React from 'react';

import { Avatar, Card, CardBody, Link, Text } from '@change/ds-core-ui';
import { TrendingIcon, VictoryFlagIcon } from '@change/ds-icons';

import { useI18n } from '@change-corgi/core-react/i18n';

import type { PetitionMarqueeCardData } from '../../hooks/usePetitionMarquee';

type Props = Readonly<{
	petition: PetitionMarqueeCardData;
	onClick: () => void;
	/**
	 * True for tiled/cloned copies rendered only to fill out the marquee ring - keeps every
	 * petition announced, and tabbed to, exactly once via aria-hidden + tabIndex. Deliberately
	 * NOT `inert`: the loop slides these copies into view, so they must stay clickable.
	 */
	duplicate?: boolean;
}>;

export const PetitionMarqueeCard: React.FC<Props> = ({ petition, onClick, duplicate = false }) => {
	const { translate } = useI18n();
	const statusLabel = petition.isVictory
		? translate('corgi.components.petition_card.victory')
		: translate('corgi.homepage.petition_marquee.trending_label');

	return (
		// Spacing is a trailing margin (not a track gap) so the -50% loop point lands exactly
		// between two cards.
		<li className="me-4 w-80 shrink-0" aria-hidden={duplicate || undefined}>
			{/* The whole card is the link: a real <a> gives keyboard activation, open-in-new-tab and
		    link semantics for free, and routes client-side via the DS RouterProvider. */}
			<Card
				as={Link}
				href={petition.href}
				onClick={onClick}
				underline={false}
				tabIndex={duplicate ? -1 : undefined}
				className="hover:elevation-low bg-background-base h-full [--card-padding:12px]"
				data-testid="petition-marquee-card"
				data-qa={`petition-marquee-card-${petition.key}`}
			>
				<CardBody className="flex flex-row items-center gap-3 px-3 text-left">
					<Avatar src={petition.imageUrl} alt="" aria-hidden size={72} className="shrink-0 object-cover" />
					<div>
						<Text
							as="span"
							variant="body"
							size="large"
							fontWeight="bold"
							className="text-typography-strong line-clamp-2 pb-0.5"
						>
							{petition.title}
						</Text>
						<span className="text-typography-weak flex items-center gap-1.5">
							{/* the icon is the only visual carrier of victory vs trending, so the status is
						    announced from text a screen reader can reach */}
							<span className="sr-only">{statusLabel}</span>
							{petition.isVictory ? (
								<VictoryFlagIcon className="text-icon-brand" size={20} />
							) : (
								<TrendingIcon className="text-icon-brand" size={20} />
							)}
							<Text as="span" variant="body" size="large">
								{petition.signaturesLabel}
							</Text>
						</span>
					</div>
				</CardBody>
			</Card>
		</li>
	);
};
